// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FeaturedProductItem from "@plumberssupply/App/Components/FeaturedProducts/FeaturedProductItem";
import usePrevious from "@plumberssupply/App/Hooks/usePrevious";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { color, styledScrollbar } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const PRODUCT_ID = "FeaturedProducts_Header";

interface OwnProps {
    productsIds?: string[];
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isMobileScreen: getXngCommon(state).isMobileScreen,
        products: getProductsForProductInfoList(state, PRODUCT_ID),
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentFeatureStyles {
    centeringWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    title?: TypographyProps;
}

export const featuredProductsStyles: ContentFeatureStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
            background: ${color("common.background")};
            border: 1px solid ${color("common.ui7")};
            border-left: none;
            max-height: 452px;
            height: 452px;
            list-style: none;
            margin: 0;
            padding: 0;
            overflow-y: auto;
            flex: 0 0 310px;
            width: 310px;
            z-index: ${getProp("theme.zIndex.menu")};
            ${styledScrollbar(5)}
        `,
    },
    title: {
        css: css`
            padding: 16px;
            margin: 0 0 4px 0;
            color: ${color("common.ui11")};
        `,
    },
};

const styles = featuredProductsStyles;

const FeaturedProducts: FC<Props> = ({ productsIds, products, loadProductInfoList, isMobileScreen }) => {
    const prevProductsIds = usePrevious(productsIds);
    useEffect(() => {
        if (!productsIds || productsIds.length === 0) {
            return;
        }

        loadProductInfoList({
            id: PRODUCT_ID,
            getProductCollectionParameter: { productNumbers: productsIds, pageSize: "9999" } as any,
        });
    }, []);

    if (!products || products?.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.centeringWrapper} data-test-selector="HeaderFeaturedProducts">
            <Typography variant="h4" {...styles.title}>
                {translate("Featured Products")}
            </Typography>
            {products?.map(product => (
                <FeaturedProductItem product={product} key={product.id} widgetId={PRODUCT_ID} />
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProducts);
